import styled from '@emotion/styled'

// Design tokens
const COLORS = {
  background: '#f4f6fb',
  white: '#ffffff',
  text: '#121212',
  textSecondary: '#666666',
  accent: '#ff5000',
  primary: '#5378fc',
  primaryDark: '#29355d',
  shadow: 'rgba(0, 0, 0, 0.08)',
  shadowElevated:
    '0px 5px 5px -3px #00000033, 0px 3px 14px 2px #0000001f, 0px 8px 10px 1px #00000024',
}

const SPACING = {
  xs: '20px',
  sm: '30px',
  md: '40px',
  lg: '50px',
  xl: '60px',
}

const FONT_FAMILY =
  "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
const TRANSITION = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'

const BREAKPOINTS = {
  tablet: '768px',
  mobile: '480px',
}

// Main container with light background matching Keap Partner pages
export const CountrySelectionContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #f4f6fb;
  padding: 80px 0;
  font-family: ${FONT_FAMILY};
`

// White card wrapper
export const CountrySelectionWrapper = styled.div`
  width: 80vw;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.25);
  padding: 80px 10px;
  max-width: 1200px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 600px;

  @media (max-width: 768px) {
    width: 95vw;
    padding: 80px 10px;
    min-height: 500px;
  }

  @media (max-width: 480px) {
    width: 95vw;
    padding: 80px 10px;
    min-height: 400px;
  }
`

// Header section
export const CountrySelectionHeader = styled.div`
  margin-bottom: ${SPACING.md};
`

// Main title
export const CountrySelectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: ${COLORS.text};
  margin: 20px 0 16px 0;
  letter-spacing: -0.75px;
  line-height: 42px;

  @media (max-width: ${BREAKPOINTS.tablet}) {
    font-size: 24px;
    line-height: 36px;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 20px;
    line-height: 30px;
  }
`

// Subtitle
export const CountrySelectionSubtitle = styled.p`
  font-size: 17px;
  color: ${COLORS.textSecondary};
  margin: 0;
  line-height: 150%;
  font-weight: 400;
  letter-spacing: -0.75px;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 14px;
  }
`

// Grid container for country buttons
export const CountryButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: ${SPACING.md};
  margin-top: ${SPACING.lg};

  @media (max-width: ${BREAKPOINTS.tablet}) {
    gap: ${SPACING.sm};
  }
`

// Country button card
export const CountryButton = styled.button`
  position: relative;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  overflow: visible;
  transition: ${TRANSITION};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;

  &:hover {
    img {
      opacity: 0.7;
      transition: 0.3s;
      box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.2);
      border-radius: 4px;
    }
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus {
    outline: none;
  }
`

// Flag image
export const CountryFlagImage = styled.img`
  width: 150px;
  height: 75px;
  object-fit: cover;
  display: block;
  transition: opacity 0.3s ease, box-shadow 0.3s ease;
  border-radius: 4px;
`

// Button content with gradient
export const CountryButtonContent = styled.div`
  padding: 0;
  width: auto;
  background: transparent;
  color: ${COLORS.text};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
`

// Country name text
export const CountryButtonText = styled.span`
  display: block;
  font-weight: 700;
  font-size: 16px;
  letter-spacing: -0.3px;
  line-height: 110%;
  color: ${COLORS.text};

  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 14px;
  }
`

// Country description text
export const CountryButtonSubtext = styled.span`
  display: block;
  font-weight: 400;
  font-size: 14px;
  opacity: 0.95;
  letter-spacing: 0.2px;
  line-height: 150%;
`

// Decorative element
export const DecorativeElement = styled.div`
  position: absolute;
  width: 100px;
  height: 100px;
  background: linear-gradient(
    135deg,
    rgba(83, 120, 252, 0.1) 0%,
    rgba(41, 53, 93, 0.1) 100%
  );
  border-radius: 50%;
  pointer-events: none;

  &.top-left {
    top: -50px;
    left: -50px;
  }

  &.bottom-right {
    bottom: -50px;
    right: -50px;
  }

  @media (max-width: ${BREAKPOINTS.tablet}) {
    display: none;
  }
`
